import { cn } from '@/lib/utils'

interface GradientAvatarProps {
  /** Seed for the gradient — usually email or stable ID. */
  seed: string
  /** Display name — used for initials when no image */
  name: string
  /** Optional image URL — overrides the gradient when present */
  src?: string | null
  /** Pixel size — defaults to 40 */
  size?: number
  /** Show a status dot in the bottom-right corner */
  status?: 'online' | 'offline' | 'away' | 'busy' | null
  className?: string
}

/**
 * Deterministic gradient avatar — every person gets a unique two-stop
 * gradient derived from their seed (email/id). Two people named "Nathan"
 * get visually distinct identities. Falls back to image if `src` is set.
 *
 * Brings life to contact lists, mention pickers, lead avatars, and
 * anywhere else AvatarFallback would otherwise render the same gradient
 * for every person.
 */
export function GradientAvatar({
  seed,
  name,
  src,
  size = 40,
  status,
  className,
}: GradientAvatarProps) {
  const gradient = generativeGradient(seed)
  const initials = getInitials(name)
  const ringColor = statusColor(status)
  const statusSize = Math.max(8, Math.round(size * 0.28))
  const fontSize = Math.max(10, Math.round(size * 0.36))

  return (
    <span className={cn('relative inline-flex shrink-0', className)} style={{ width: size, height: size }}>
      <span
        className="block rounded-full overflow-hidden ring-2 ring-background"
        style={{ width: size, height: size, background: src ? undefined : gradient }}
      >
        {src ? (
          <img src={src} alt={name} className="h-full w-full object-cover" draggable={false} />
        ) : (
          <span
            className="flex h-full w-full items-center justify-center text-white font-semibold tracking-tight select-none"
            style={{ fontSize, textShadow: '0 1px 2px rgba(0,0,0,0.18)' }}
          >
            {initials}
          </span>
        )}
      </span>
      {status && status !== 'offline' && (
        <span
          className={cn('absolute bottom-0 right-0 rounded-full ring-2 ring-background', ringColor)}
          style={{ width: statusSize, height: statusSize }}
          aria-label={status}
        />
      )}
    </span>
  )
}

// djb2-ish hash — small, stable, no deps
function hashString(s: string): number {
  let hash = 5381
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash) + s.charCodeAt(i)
    hash |= 0 // 32-bit
  }
  return Math.abs(hash)
}

/** Two-stop linear gradient with two distinct, harmonious hues. */
export function generativeGradient(seed: string): string {
  const h = hashString(seed || 'unknown')
  const hue1 = h % 360
  // Second hue is 25–85° away from the first — close enough to feel
  // related, far enough to read as a real gradient (not a single color).
  const hue2 = (hue1 + 25 + (h % 60)) % 360
  // Fixed saturation/lightness so every gradient feels like it belongs
  // to the same family (no neon, no muddy).
  return `linear-gradient(135deg, hsl(${hue1} 68% 56%) 0%, hsl(${hue2} 72% 46%) 100%)`
}

function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function statusColor(status?: string | null): string {
  switch (status) {
    case 'online': return 'bg-emerald-500'
    case 'busy':   return 'bg-rose-500'
    case 'away':   return 'bg-amber-500'
    default:       return 'bg-muted-foreground/60'
  }
}
